import React, { useState, useContext, useEffect, useRef } from "react";
import { connect, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { SITEKEY } from "../../context/constValues";
import { loginConfirmedAction } from "../../store/actions/AuthActions";
import Swal from "sweetalert2";
import Turnstile from "react-turnstile";
import BgImage from "../../images/bg.png";
import pol from "../../images/pol.jpg";
import {
  getPasskeyAuthenticationOptions,
  getPasskeyRegistrationOptions,
  loginAdmin,
  verifyPasskeyAuthentication,
  verifyPasskeyRegistration,
} from "../../api/AdminApi";
import AuthContext from "../../context/auth-context";
import { FaKey } from "react-icons/fa";
import {
  isPasskeySupported,
  offerPasswordSave,
  prepareCreationOptions,
  prepareRequestOptions,
  serializeCredential,
} from "../../utils/passkeys";
import "./Login.css";

function Login() {
  const history = useHistory();
  const [rememberMe, setRememberMe] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [savePasskey, setSavePasskey] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const authCtx = useContext(AuthContext);
  const dispatch = useDispatch();
  const turnstileRef = useRef();

  

  // Carga datos guardados si "Remember Me" estaba activo
  useEffect(() => {
    const storedEmail = localStorage.getItem("rememberEmail");
    const storedPassword = localStorage.getItem("rememberPassword");
    if (storedEmail) {
      setEmail(storedEmail);
      setRememberMe(true);
    }
    if (storedPassword) setPassword(storedPassword);
  }, []);

  // Limpia campos si se desmarca "Remember Me"
  const handleRememberChange = (checked) => {
    setRememberMe(checked);
    if (!checked) {
      localStorage.removeItem("rememberEmail");
      localStorage.removeItem("rememberPassword");
    }
  };

  const finishLogin = async (response, remember = true) => {
    const expiration = new Date(Date.now() + 50 * 60 * 1000).toISOString();
    await authCtx.login(response.token, expiration, remember, response.userData);
    dispatch(loginConfirmedAction({ idToken: response.token }));
  };

  const registerPasskey = async () => {
    const options = await getPasskeyRegistrationOptions();
    const credential = await navigator.credentials.create({
      publicKey: prepareCreationOptions(options.publicKey),
    });
    if (!credential) throw new Error("No se creó la llave de acceso");
    await verifyPasskeyRegistration({
      challenge: options.challenge,
      credential: serializeCredential(credential),
      deviceName: navigator.platform || "Este dispositivo",
    });
  };

  const onLogin = async (e) => {
    e.preventDefault();

    if (!isVerified || !turnstileToken) {
      Swal.fire({ title: "Error", text: "Por favor completa la verificación de seguridad", icon: "error", confirmButtonText: "Aceptar" });
      return;
    }

    Swal.fire({ title: "Ingresando", didOpen: () => Swal.showLoading() });

    try {
      const response = await loginAdmin({ usuario: email, password });
      if (response.token) {
        await finishLogin(response, rememberMe);

        if (rememberMe) {
          localStorage.setItem("rememberEmail", email);
          localStorage.setItem("rememberPassword", password);
          await offerPasswordSave(email, password);
        }
        if (savePasskey && isPasskeySupported()) {
          try {
            await registerPasskey();
            await Swal.fire({
              icon: "success",
              title: "Llave de acceso guardada",
              text: "La próxima vez podrás ingresar con Windows Hello, Touch ID o el método de seguridad de tu dispositivo.",
              confirmButtonText: "Continuar",
            });
          } catch (passkeyError) {
            if (passkeyError?.name !== "NotAllowedError") {
              await Swal.fire({
                icon: "warning",
                title: "Ingresaste correctamente",
                text: `No se pudo guardar la llave de acceso: ${passkeyError.message}`,
                confirmButtonText: "Continuar",
              });
            }
          }
        }
        history.push("/sesiones");
        Swal.close();
      }
    } catch (error) {
      const errorMessage = error.message || "Error en el inicio de sesión. Por favor intenta de nuevo.";
      // Mostrar error sin deshabilitar el botón de ingresar
      Swal.fire({ title: "Error", text: errorMessage, icon: "error", confirmButtonText: "Aceptar" });
      // No resetear isVerified ni turnstileToken para mantener el botón habilitado
    }
  };

  const onPasskeyLogin = async () => {
    if (!isPasskeySupported()) {
      Swal.fire({ icon: "info", title: "No disponible", text: "Este navegador o conexión no admite llaves de acceso.", confirmButtonText: "Aceptar" });
      return;
    }
    setPasskeyBusy(true);
    try {
      const options = await getPasskeyAuthenticationOptions(email.trim());
      const credential = await navigator.credentials.get({
        publicKey: prepareRequestOptions(options.publicKey),
      });
      if (!credential) throw new Error("No se seleccionó una llave de acceso");
      const response = await verifyPasskeyAuthentication({
        challenge: options.challenge,
        credential: serializeCredential(credential),
      });
      await finishLogin(response, true);
      if (response.userData?.Usuario) localStorage.setItem("rememberEmail", response.userData.Usuario);
      history.push("/sesiones");
    } catch (error) {
      if (error?.name !== "NotAllowedError") {
        Swal.fire({ icon: "error", title: "No se pudo ingresar", text: error.message, confirmButtonText: "Aceptar" });
      }
    } finally {
      setPasskeyBusy(false);
    }
  };

  return (
    <div className="container login-page">
      <div className="row w-100 align-items-center justify-content-center">
        <div className="col-xl-12">
          <div className="card">
            <div className="card-body p-0">
              <div className="row m-0 login-card-row">
                <div
                  className="col-xl-6 col-md-6 sign text-center sign-bg d-flex align-items-center justify-content-center"
                  style={{ backgroundImage: `url(${pol})` }}
                >
                  <img
                    src={BgImage}
                    alt="Empresa"
                    style={{ width: 300, display: 'block', margin: 'auto' }}
                  />
                </div>
                <div className="col-xl-6 col-md-6 login-form-column">
                  <div className="sign-in-your px-2">
                    <h4 className="fs-20">Ingrese con su Cuenta</h4>
                    <span>Bienvenido! loguea con los datos proporcionados</span>
                    <form onSubmit={onLogin} autoComplete="on">
                      <div className="mb-3">
                        <label className="mb-1"><strong>Cuenta</strong></label>
                        <input
                          required
                          type="text"
                          id="username"
                          name="username"
                          autoComplete="username webauthn"
                          className="form-control"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="Tu cuenta"
                        />
                      </div>
                      <div className="mb-3">
                        <label className="mb-1"><strong>Contraseña</strong></label>
                        <input
                          required
                          type="password"
                          id="current-password"
                          name="password"
                          autoComplete="current-password"
                          className="form-control"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Tu contraseña"
                        />
                      </div>
                      <div className="row d-flex justify-content-between mt-4 mb-2">
                        <div className="form-check custom-checkbox ms-1">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="remember_checkbox"
                            checked={rememberMe}
                            onChange={(e) => handleRememberChange(e.target.checked)}
                          />
                          <label className="form-check-label" htmlFor="remember_checkbox">
                            Recuérdame
                          </label>
                        </div>
                      </div>
                      {isPasskeySupported() && (
                        <div className="form-check custom-checkbox ms-1 mb-3">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="save_passkey_checkbox"
                            checked={savePasskey}
                            onChange={(event) => setSavePasskey(event.target.checked)}
                          />
                          <label className="form-check-label" htmlFor="save_passkey_checkbox">
                            Guardar una llave de acceso
                            <span className="passkey-hint">Windows Hello, Touch ID, iCloud Keychain o Android</span>
                          </label>
                        </div>
                      )}
                      <div className="text-center mb-2">
                        <button
                          type="submit"
                          className="btn btn-primary btn-block"
                          disabled={!isVerified}
                        >
                          Ingresar
                        </button>
                      </div>
                      {isPasskeySupported() && (
                        <>
                          <div className="passkey-divider"><span>o</span></div>
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-block passkey-button mb-3"
                            onClick={onPasskeyLogin}
                            disabled={passkeyBusy}
                          >
                            <FaKey className="me-2" aria-hidden="true" />
                            {passkeyBusy ? "Verificando…" : "Ingresar con llave de acceso"}
                          </button>
                        </>
                      )}
                     { <Turnstile
                        ref={turnstileRef}
                        theme="light"
                        sitekey={SITEKEY}
                        onVerify={(token) => { setTurnstileToken(token); setIsVerified(true); }}
                        onError={() => { setIsVerified(false); setTurnstileToken(null); }}
                        onExpire={() => { setIsVerified(false); setTurnstileToken(null); }}
                      /> }
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({
  errorMessage: state.auth.errorMessage,
  successMessage: state.auth.successMessage,
  showLoading: state.auth.showLoading
});

export default connect(mapStateToProps)(Login);
